import { PlatformService } from "./platform.service";

export enum ChatWindowState {
  Open = 'open',
  Minimized = 'minimized',
  Closed = 'closed'
}

export enum InputState {
  Shown = 'shown',
  Hidden = 'hidden'
}

export enum HeaderState {
  Shown = 'shown',
  Hidden = 'hidden'
}

enum NameSpaceSuffix {
  ActiveSessionId = 'activeSessionId',
  WindowState = 'windowState',
  InputState = 'inputState',
  LoginTriggered = 'loginTriggered',
  IsNewSession = 'isNewSession',
  HeaderState = 'headerState',
}

export class ChatSessionService {
  private readonly prefix = 'swp-chatbot';

  private namespace: string;
  private activeSessionId: string;
  private windowState: string;
  private inputState: string;
  private loginTriggered: string;
  private isNewSession: string;
  private headerState: string;
  private platformService: PlatformService;

  constructor(endpoint: string) {
    this.namespace = `${this.prefix}:${endpoint}`;

    this.activeSessionId = `${this.namespace}:${NameSpaceSuffix.ActiveSessionId}`;
    this.windowState = `${this.namespace}:${NameSpaceSuffix.WindowState}`;
    this.inputState = `${this.namespace}:${NameSpaceSuffix.InputState}`;
    this.loginTriggered = `${this.namespace}:${NameSpaceSuffix.LoginTriggered}`;
    this.isNewSession = `${this.namespace}:${NameSpaceSuffix.IsNewSession}`;
    this.headerState = `${this.namespace}:${NameSpaceSuffix.HeaderState}`;

    this.platformService = new PlatformService();
  }

  static retrieveEntireSession(): string {
    return encodeURIComponent(JSON.stringify(sessionStorage));
  }

  createNewSessionId(): string {
    const sessionId = `session-${crypto.randomUUID()}`;
    sessionStorage.setItem(this.activeSessionId, sessionId);
    sessionStorage.setItem(this.isNewSession, 'true');

    this.updateHeaderState();

    return sessionId;
  }

  getSessionId(): string | undefined {
    return sessionStorage.getItem(this.activeSessionId) || undefined;
  }

  popIsNewSession(): boolean {
    const isNewSession = sessionStorage.getItem(this.isNewSession);
    sessionStorage.removeItem(this.isNewSession);

    return isNewSession === 'true' || false;
  }

  reset(): void {
    sessionStorage.removeItem(this.activeSessionId);
    sessionStorage.removeItem(this.windowState);
    sessionStorage.removeItem(this.inputState);
    sessionStorage.removeItem(this.isNewSession);
    sessionStorage.removeItem(this.headerState);
  }

  setWindowState(state: ChatWindowState): void {
    sessionStorage.setItem(this.windowState, state);
  }

  getWindowState(): ChatWindowState {
    const windowState = sessionStorage.getItem(this.windowState) as ChatWindowState;
    return windowState ?? ChatWindowState.Closed;
  }

  setInputState(state: InputState): void {
    sessionStorage.setItem(this.inputState, state);

    const inputField = document.querySelector("[data-cognigy-webchat-root] [data-cognigy-webchat].webchat .webchat-input :first-child") as HTMLElement;
    inputField.style.display = state === InputState.Hidden ? 'none' : 'initial';
  }

  getInputState(): InputState | null {
    return sessionStorage.getItem(this.inputState) as InputState | null;
  }

  pushLoginTriggered() {
    sessionStorage.setItem(this.loginTriggered, 'true');
  }

  popLoginTriggered() {
    const loginTriggered = sessionStorage.getItem(this.loginTriggered);
    sessionStorage.removeItem(this.loginTriggered);
    return loginTriggered === 'true' || false;
  }

  getHeaderState(): HeaderState {
    // by default the header is always shown
    return sessionStorage.getItem(this.headerState) as HeaderState || HeaderState.Shown;
  }

  updateHeaderState() {
    const state = this.platformService.isMobileAppEnvironment() ? HeaderState.Hidden : HeaderState.Shown;
    sessionStorage.setItem(this.headerState, state);
  }

  async restoreSessionFromApp(): Promise<string | undefined> {
    const getFromAndroid = (window as any).Android?.getSavedChatbotUserSession;
    const getFromIos = (window as any).webkit?.messageHandlers?.getSavedChatbotUserSession;

    if (!(getFromAndroid && typeof getFromAndroid === 'function') && !(getFromIos && typeof getFromIos?.postMessage === 'function')) {
      console.debug('There is no function to restore saved user session');
      return;
    }

    const getSavedSession = async (): Promise<string> => {
      if (getFromAndroid) {
        // important notes for Android:
        // - context of the latest function must be bound to its parent;
        return getFromAndroid.bind((window as any).Android)();
      }

      if (getFromIos) {
        // important notes for iOS:
        // - it must be resolved since it returns a promise instead of final value;
        // - calling with an argument is critically important!;
        // - context of the latest function must be bound to its parent
        return getFromIos.postMessage.bind(getFromIos)('');
      }

      return '';
    }

    try {
      console.debug('Retrieve saved user session from App and setting it to chatbot');
      const savedSession = await getSavedSession();

      if (!savedSession || typeof savedSession !== 'string') {
        console.debug('There is no saved user session');
        return;
      }

      const sessionObject = JSON.parse(decodeURIComponent(savedSession));
      let sessionId: string;
      sessionStorage.clear(); // remove any previous data dut to potential irrelevance to restored session

      for (const key in sessionObject) {
        sessionStorage.setItem(key, sessionObject[key]);

        if (key.endsWith(`:${NameSpaceSuffix.ActiveSessionId}`)) {
          sessionId = sessionObject[key];
        }
      }

      console.debug('User session restored successfully. Session ID:', sessionId);
      return sessionId;
    } catch (error: any) {
      sessionStorage.clear(); // don't keep anything if something went wrong during session restoring
      console.debug('User session was not restored. Error is below');
      console.error(error);
    }
  }
}
